import { AdultProfile } from '@yoyoclub/shared';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { adultProfileApi } from '../../services/profileApi';
import Button from '../ui/Button';
import Card from '../ui/Card';

export default function AdultProfileFormModal({
  profile,
  onClose,
}: {
  profile: AdultProfile | null;
  onClose: () => void;
}) {
  const { t } = useTranslation(['profiles']);
  const [formData, setFormData] = useState({
    name: profile?.name || '',
    phone: profile?.phone || '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const profileData = {
        name: formData.name,
        phone: formData.phone,
      };

      if (profile) {
        await adultProfileApi.updateProfile(profile.id, profileData);
      } else {
        await adultProfileApi.createProfile(profileData);
      }

      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save adult profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            {profile ? t('profiles:adult.editTitle') : t('profiles:adult.addTitle')}
          </h2>

          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('profiles:adult.name')} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder={t('profiles:adult.namePlaceholder')}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('profiles:adult.phone')} <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                required
                value={formData.phone}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    phone: e.target.value.replace(/[^\d+\-()（）#转 ]/g, ''),
                  })
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder={t('profiles:adult.phonePlaceholder')}
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="submit" variant="primary" disabled={loading} className="flex-1">
                {loading ? t('profiles:adult.saving') : t('profiles:adult.save')}
              </Button>
              <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
                {t('profiles:adult.cancel')}
              </Button>
            </div>
          </form>
        </div>
      </Card>
    </div>
  );
}
